import React, { useCallback, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import Alert from 'components/Alert'
import {
  fetchRFPById,
  partialUpdateRFP,
  downloadAwardedCarriers,
} from 'features/requestForProposalList/actions'
import {
  rfpDetailSelector,
  rfpDetailIsLoadingSelector,
  rfpDetailIsExportingSelector,
  rfpDetailErrorSelector,
} from 'features/requestForProposalList/selectors'
import {
  fetchLaneProposals,
  partialUpdateProposal,
} from 'features/laneProposals/actions'
import {
  laneProposalsSelector,
  laneProposalsIsLoadingSelector,
} from 'features/laneProposals/selectors'
import analytics, { AnalyticsEvent } from 'utils/analytics'
import { isAwardable } from 'utils/rfp'
import { useModal } from 'hooks/useModal'
import { RFP_STATE } from '../../../constants'
import LanesTable from './LanesTable'
import RFPInfo from './RFPInfo'
import LaneProposals from './LaneProposals'
import AwardDialog from './AwardDialog'

const Wrapper = styled.div`
  display: flex;
  color: ${({ theme }) => theme.colors.text};

  a {
    text-decoration: none;
    margin-bottom: 5px;
    color: ${({ theme }) => theme.colors.text};

    :visited {
      color: ${({ theme }) => theme.colors.text};
    }
  }
`
const RFPData = styled.div`
  flex-grow: 5;
`
export const RFPDetailActionsContext = React.createContext<{
  partialUpdateLaneProposal: (
    proposalId: number,
    proposal: Partial<LaneProposal>
  ) => void
}>({
  partialUpdateLaneProposal: () => undefined,
})
export default function RFPDetail() {
  const { rfpId } = useParams<{ rfpId: string }>()
  const dispatch = useDispatch()
  const isLoading = useSelector(rfpDetailIsLoadingSelector)
  const isExporting = useSelector(rfpDetailIsExportingSelector)
  const rfp = useSelector(rfpDetailSelector)
  const rfpDetailError = useSelector(rfpDetailErrorSelector)
  const proposals = useSelector(laneProposalsSelector)
  const proposalsIsloading = useSelector(laneProposalsIsLoadingSelector)

  const { modalState, closeModal, openModal } = useModal()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedLane, setSelectedLane] = useState<Lane>()
  const [previousLane, setPreviousLane] = useState<Lane>()
  const [nextLane, setNextLane] = useState<Lane>()
  const lanes = useMemo(() => Object.values(rfp?.lanes || {}), [rfp])

  const publishRFP = useCallback(() => {
    if (rfp) {
      dispatch(
        partialUpdateRFP(
          rfp.id,
          { state: RFP_STATE.PUBLISHED },
          {
            type: 'success',
            message: 'The RFP is now published.',
            description:
              'Click on the right panel to follow responses for each lane.',
          }
        )
      )
    }
  }, [dispatch, rfp])

  const finalizeRFP = useCallback(() => {
    if (rfp) {
      dispatch(partialUpdateRFP(rfp.id, { state: RFP_STATE.FINALIZED }))
    }
  }, [dispatch, rfp])

  const showLaneProposals = useCallback(
    (lane: Lane) => {
      setIsOpen(true)
      if (selectedLane?.id !== lane.id) {
        setSelectedLane(lane)
        dispatch(fetchLaneProposals(lane.id))
      }
      analytics.track(AnalyticsEvent.OpenCarrierBids, {
        rfpId,
        laneId: lane.id,
      })
      const currentLaneIndex = lanes.findIndex((l) => lane.id === l.id)
      const hasPrevious = currentLaneIndex > 0
      const hasNext = currentLaneIndex > -1 && currentLaneIndex < lanes.length
      setPreviousLane(hasPrevious ? lanes[currentLaneIndex - 1] : undefined)
      setNextLane(hasNext ? lanes[currentLaneIndex + 1] : undefined)
    },
    [dispatch, selectedLane, rfpId, lanes]
  )

  const toggleVisible = useCallback(() => {
    const event = isOpen
      ? AnalyticsEvent.CloseCarrierBids
      : AnalyticsEvent.OpenCarrierBids

    if (isOpen) {
      setIsOpen(false)
      analytics.track(event, { rfpId, laneId: undefined })
      return
    }

    showLaneProposals(selectedLane || lanes[0])
  }, [isOpen, rfpId, lanes, selectedLane, showLaneProposals])

  const partialUpdateLaneProposal = useCallback(
    (proposalId: number, partialProposal: Partial<LaneProposal>) => {
      if (selectedLane && isAwardable(rfp?.state)) {
        dispatch(
          partialUpdateProposal(selectedLane.id, proposalId, partialProposal)
        )
      }
    },
    [dispatch, rfp, selectedLane]
  )

  const exportAwardedCarriers = useCallback(() => {
    if (rfp) dispatch(downloadAwardedCarriers(rfp))
  }, [dispatch, rfp])

  const closeLaneProposals = useCallback(
    (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        setIsOpen(false)
        analytics.track(AnalyticsEvent.CloseCarrierBids, {
          rfpId,
        })
      }
    },
    [isOpen, rfpId]
  )

  useEffect(() => {
    dispatch(fetchRFPById(rfpId))
  }, [dispatch, rfpId])

  useEffect(() => {
    window.addEventListener('keydown', closeLaneProposals)

    return () => window.removeEventListener('keydown', closeLaneProposals)
  }, [closeLaneProposals])

  return (
    <RFPDetailActionsContext.Provider value={{ partialUpdateLaneProposal }}>
      {rfpDetailError && <Alert type="error">{rfpDetailError}</Alert>}
      <Wrapper>
        {rfp && modalState && (
          <AwardDialog
            rfp={rfp}
            isOpen={modalState}
            onConfirm={finalizeRFP}
            onClose={closeModal}
          />
        )}
        <RFPData>
          <RFPInfo
            rfp={rfp}
            isLoading={isLoading}
            isExporting={isExporting}
            onClickPublish={publishRFP}
            onClickFinish={openModal}
            onClickExport={exportAwardedCarriers}
          />
          <LanesTable
            lanes={lanes}
            isLoading={isLoading}
            selectedId={selectedLane?.id}
            onSelect={showLaneProposals}
          />
        </RFPData>
        {rfp && rfp.state !== RFP_STATE.DRAFT && (
          <LaneProposals
            isOpen={isOpen}
            lane={selectedLane}
            proposals={proposals}
            isLoading={proposalsIsloading}
            onToggleVisible={toggleVisible}
            selectLane={showLaneProposals}
            nextLane={nextLane}
            previousLane={previousLane}
          />
        )}
      </Wrapper>
    </RFPDetailActionsContext.Provider>
  )
}
